import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import { getToken, getUser } from '../auth';
import '../styles/TechnicianProfile.css';

function TechnicianProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [technician, setTechnician] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Modals
  const [showRateModal, setShowRateModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  
  // Forms
  const [ratingForm, setRatingForm] = useState({ rating: 5, review: '' });
  const [reportForm, setReportForm] = useState({ reason: '', description: '' });
  const [messageForm, setMessageForm] = useState({ content: '' });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [notice, setNotice] = useState(null);

  const user = getUser();
  const token = getToken();

  useEffect(() => {
    if (!token || user.role !== 'hirer') {
      navigate('/hirer/signin');
      return;
    }
    fetchTechnicianProfile();
    fetchRatings();
  }, [id]);

  const fetchTechnicianProfile = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/technician/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          navigate('/hirer/signin');
          return;
        }
        throw new Error('Failed to fetch technician profile');
      }
      
      const data = await res.json();
      setTechnician(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRatings = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/technician/${id}/ratings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (res.ok) {
        const data = await res.json();
        setRatings(data.ratings || []);
      }
    } catch (err) {
      console.error('Failed to fetch ratings:', err);
    }
  };

  const handleHire = async () => {
    if (!window.confirm('Send hire request to this technician?')) return;
    
    setSubmitting(true);
    setSubmitError('');
    
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/hire`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ technician_id: parseInt(id) }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to send hire request');
      }

      setNotice({ type: 'success', text: 'Hire request sent. Waiting for technician approval.' });
      setTimeout(() => setNotice(null), 2400);
      navigate('/hirer/messages', {
        state: {
          startConversationWith: {
            id: parseInt(id, 10),
            name: technician?.full_name || 'Technician',
            role: 'technician',
          },
        },
      });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          technician_id: parseInt(id),
          rating: ratingForm.rating,
          review: ratingForm.review.trim() || null,
        }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to submit rating');
      }
      
      alert('Rating submitted successfully!');
      setShowRateModal(false);
      setRatingForm({ rating: 5, review: '' });
      fetchTechnicianProfile();
      fetchRatings();
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          reported_id: parseInt(id),
          reported_role: 'technician',
          reason: reportForm.reason,
          description: reportForm.description.trim() || null,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to submit report');
      }
      
      alert('Report submitted successfully!');
      setShowReportModal(false);
      setReportForm({ reason: '', description: '' });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    
    try {
      const res = await fetch(`${API_BASE_URL}/hirer/message/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          receiver_id: parseInt(id),
          receiver_role: 'technician',
          content: messageForm.content.trim(),
        }),
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to send message');
      }
      
      setShowMessageModal(false);
      setMessageForm({ content: '' });
      setNotice({ type: 'success', text: 'Message sent successfully.' });
      setTimeout(() => setNotice(null), 2200);
      
      // Navigate to Messages page with the technician ID to auto-select conversation
      navigate('/hirer/messages', { state: { startConversationWith: { id: parseInt(id), name: technician?.full_name || 'Technician', role: 'technician' } } });
    } catch (err) {
      setSubmitError(err.message);
      setNotice({ type: 'error', text: err.message || 'Failed to send message.' });
      setTimeout(() => setNotice(null), 2500);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="tp-loading">Loading technician profile...</div>;
  }

  if (error) {
    return (
      <div className="tp-error">
        <p>{error}</p>
        <button onClick={() => navigate('/hirer/dashboard')}>Back to Dashboard</button>
      </div>
    );
  }

  if (!technician) {
    return <div className="tp-error">Technician not found</div>;
  }

  return (
    <div className="tp-container">
      {notice && (
        <div className={`tp-notice tp-notice--${notice.type}`} role="status">
          {notice.text}
        </div>
      )}
      <button className="tp-back-btn" onClick={() => navigate('/hirer/dashboard')}>
        ← Back to Dashboard
      </button>

      <div className="tp-profile">
        <div className="tp-header">
          <div className="tp-header-left">
            <h1 className="tp-name">{technician.full_name}</h1>
            <p className="tp-type">{technician.tech_type === 'other' ? technician.custom_tech_type : technician.tech_type}</p>
            <p className="tp-location">
              📍 {technician.city}, {technician.province}
            </p>
            {technician.avg_rating && (
              <div className="tp-rating-badge">
                <span className="tp-stars">★</span>
                <span className="tp-rating-value">{technician.avg_rating}</span>
                <span className="tp-rating-count">({technician.total_ratings})</span>
              </div>
            )}
          </div>
          <div className="tp-header-right">
            <button className="tp-btn tp-btn--primary" onClick={handleHire} disabled={submitting}>
              Request Hire
            </button>
            <button
              className="tp-btn tp-btn--secondary"
              onClick={() =>
                navigate('/hirer/messages', {
                  state: {
                    startConversationWith: {
                      id: parseInt(id, 10),
                      name: technician.full_name || 'Technician',
                      role: 'technician',
                    },
                  },
                })
              }
            >
              Contact
            </button>
          </div>
        </div>

        <div className="tp-details">
          <div className="tp-detail-card">
            <h3 className="tp-detail-title">Contact Information</h3>
            <div className="tp-detail-row">
              <span className="tp-detail-label">Name:</span>
              <span className="tp-detail-value">{technician.full_name}</span>
            </div>
            <div className="tp-detail-row">
              <span className="tp-detail-label">Phone:</span>
              <span className="tp-detail-value">
                {technician.phone || 'Available after hire is approved'}
              </span>
            </div>
            <div className="tp-detail-row">
              <span className="tp-detail-label">Address:</span>
              <span className="tp-detail-value">{technician.address}</span>
            </div>
            <div className="tp-detail-row">
              <span className="tp-detail-label">Location:</span>
              <span className="tp-detail-value">
                {technician.city}, {technician.province}, {technician.country}
              </span>
            </div>
          </div>

          {technician.certificate_path && (
            <div className="tp-detail-card tp-verified-card">
              <div className="tp-verified-badge">
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                  <circle cx="12" cy="12" r="11" fill="#00b481"/>
                  <path d="M7.5 12.5l3 3 6-6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="tp-verified-text">Verified &amp; Certified</span>
              </div>
              <p className="tp-verified-sub">This technician's credentials have been reviewed and approved by SkillArea.</p>
            </div>
          )}

          {(technician.categories || []).length > 0 && (
            <div className="tp-detail-card">
              <h3 className="tp-detail-title">Approved Categories</h3>
              <div className="tp-categories-list">
                {technician.categories.map((cat) => (
                  <span key={cat} className="tp-category-chip">{cat}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="tp-actions-bar">
          <button className="tp-action-btn" onClick={() => setShowRateModal(true)}>
            ⭐ Rate Technician
          </button>
          <button className="tp-action-btn tp-action-btn--danger" onClick={() => setShowReportModal(true)}>
            🚩 Report
          </button>
        </div>

        <div className="tp-reviews">
          <h2 className="tp-reviews-title">Reviews ({ratings.length})</h2>
          {ratings.length === 0 ? (
            <p className="tp-no-reviews">No reviews yet</p>
          ) : (
            <div className="tp-reviews-list">
              {ratings.map((r) => (
                <div key={r.id} className="tp-review-card">
                  <div className="tp-review-header">
                    <span className="tp-review-name">{r.hirer_name}</span>
                    <span className="tp-review-rating">{'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}</span>
                  </div>
                  {r.review && <p className="tp-review-text">{r.review}</p>}
                  <span className="tp-review-date">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rate Modal */}
      {showRateModal && (
        <div className="tp-modal-overlay" onClick={() => setShowRateModal(false)}>
          <div className="tp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tp-modal-close" onClick={() => setShowRateModal(false)}>×</button>
            <h2 className="tp-modal-title">Rate {technician.full_name}</h2>
            <form onSubmit={handleRate}>
              <div className="tp-form-group">
                <label>Rating (1-5 stars)</label>
                <select
                  value={ratingForm.rating}
                  onChange={(e) => setRatingForm({ ...ratingForm, rating: parseInt(e.target.value) })}
                  required
                >
                  <option value={5}>5 - Excellent</option>
                  <option value={4}>4 - Very Good</option>
                  <option value={3}>3 - Good</option>
                  <option value={2}>2 - Fair</option>
                  <option value={1}>1 - Poor</option>
                </select>
              </div>
              <div className="tp-form-group">
                <label>Review (optional)</label>
                <textarea
                  value={ratingForm.review}
                  onChange={(e) => setRatingForm({ ...ratingForm, review: e.target.value })}
                  rows={4}
                  placeholder="Share your experience..."
                />
              </div>
              {submitError && <div className="tp-error-msg">{submitError}</div>}
              <div className="tp-modal-actions">
                <button type="button" className="tp-btn tp-btn--outline" onClick={() => setShowRateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="tp-btn tp-btn--primary" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Rating'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {showReportModal && (
        <div className="tp-modal-overlay" onClick={() => setShowReportModal(false)}>
          <div className="tp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tp-modal-close" onClick={() => setShowReportModal(false)}>×</button>
            <h2 className="tp-modal-title">Report {technician.full_name}</h2>
            <form onSubmit={handleReport}>
              <div className="tp-form-group">
                <label>Reason *</label>
                <input
                  type="text"
                  value={reportForm.reason}
                  onChange={(e) => setReportForm({ ...reportForm, reason: e.target.value })}
                  placeholder="e.g., Unprofessional behavior"
                  required
                />
              </div>
              <div className="tp-form-group">
                <label>Description (optional)</label>
                <textarea
                  value={reportForm.description}
                  onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                  rows={4}
                  placeholder="Provide more details..."
                />
              </div>
              {submitError && <div className="tp-error-msg">{submitError}</div>}
              <div className="tp-modal-actions">
                <button type="button" className="tp-btn tp-btn--outline" onClick={() => setShowReportModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="tp-btn tp-btn--danger" disabled={submitting}>
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && (
        <div className="tp-modal-overlay" onClick={() => setShowMessageModal(false)}>
          <div className="tp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="tp-modal-close" onClick={() => setShowMessageModal(false)}>×</button>
            <h2 className="tp-modal-title">Message {technician.full_name}</h2>
            <form onSubmit={handleSendMessage}>
              <div className="tp-form-group">
                <label>Message *</label>
                <textarea
                  value={messageForm.content}
                  onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                  rows={5}
                  placeholder="Type your message..."
                  required
                />
              </div>
              {submitError && <div className="tp-error-msg">{submitError}</div>}
              <div className="tp-modal-actions">
                <button type="button" className="tp-btn tp-btn--outline" onClick={() => setShowMessageModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="tp-btn tp-btn--primary" disabled={submitting}>
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechnicianProfile;
